/**
 * A self-healing pool of Binance combined-stream WebSockets.
 *
 * The requirement is "reconnects cleanly when a socket drops", and there are
 * more ways for that to happen than a visible close event:
 *
 *   1. Binance force-closes every connection at the 24h mark.
 *   2. A laptop sleeps; the socket looks open but is a black hole.
 *   3. Wi-Fi flaps; `close` fires seconds-to-minutes late, if at all.
 *   4. The tab is backgrounded and throttled.
 *
 * Only case 1 and 3 produce a `close` event we can react to. So alongside the
 * normal close/error handling there is a watchdog: these streams are never
 * silent for long (a `@ticker` alone ticks once a second), so if nothing has
 * arrived in `staleMs` the connection is presumed dead and torn down
 * regardless of what `readyState` claims. That is what catches the sleep case,
 * which is otherwise the one users actually hit.
 *
 * Reconnection is exponential with jitter — jitter matters because without it
 * every shard in the pool retries on exactly the same schedule and hammers the
 * endpoint in lockstep. Hosts rotate after repeated failures so a single
 * blocked or degraded edge does not strand the terminal.
 *
 * Streams are sharded across sockets. One socket for everything is simpler,
 * but it makes every drop a total blackout and puts all message parsing behind
 * one event source.
 */

import { Emitter } from '../lib/emitter';
import { Rolling } from '../lib/ring';
import { MAX_STREAMS_PER_SOCKET, WS_HOSTS, serverNow, type StreamEnvelope } from './binance';

export type ConnState = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'offline';

export interface PoolStatus {
  state: ConnState;
  /** Sockets currently in the OPEN readyState. */
  open: number;
  total: number;
  streams: number;
  /** Messages per second across the pool, smoothed. */
  msgRate: number;
  /** Median (Date.now() - eventTime) in ms. Includes clock skew. */
  latency: number;
  reconnects: number;
  lastError: string | null;
}

type Router = (stream: string, data: unknown) => void;

const BASE_BACKOFF = 600;
const MAX_BACKOFF = 20_000;
/** No frame at all for this long means the socket is dead, whatever it says. */
const STALE_MS = 25_000;
/** Reconnect ahead of Binance's own 24h cutoff, at a moment we choose. */
const PROACTIVE_CYCLE_MS = 23 * 60 * 60 * 1000;
/** Binance allows 5 control messages/sec; stay under it. */
const CONTROL_INTERVAL_MS = 280;

class ManagedSocket {
  private ws: WebSocket | null = null;
  private streams = new Set<string>();
  /**
   * The stream set the in-flight (or current) connection's URL was built from.
   * Diffed against `streams` on open to catch anything added or removed while
   * the handshake was still in progress.
   */
  private urlStreams = new Set<string>();
  private connectTimer = 0;
  private attempt = 0;
  private hostIndex = 0;
  private reconnectTimer = 0;
  private watchdogTimer = 0;
  private cycleTimer = 0;
  private controlTimer = 0;
  private pendingSub: string[] = [];
  private pendingUnsub: string[] = [];
  private nextId = 1;
  private closedByUs = false;
  private lastMessageAt = 0;

  state: ConnState = 'idle';

  constructor(
    readonly id: number,
    private readonly router: Router,
    private readonly onChange: () => void,
    private readonly onMessageMeta: (eventTime: number | undefined, eventType?: string) => void,
    private readonly onError: (msg: string) => void,
  ) {}

  get size(): number {
    return this.streams.size;
  }

  get isOpen(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  has(stream: string): boolean {
    return this.streams.has(stream);
  }

  add(stream: string): void {
    if (this.streams.has(stream)) return;
    this.streams.add(stream);
    if (this.isOpen) {
      this.pendingSub.push(stream);
      this.scheduleControl();
      return;
    }
    if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
      // The URL of the connection already in flight cannot be changed. Do
      // nothing here: `onopen` diffs `streams` against `urlStreams` and
      // subscribes whatever arrived late. Calling connect() here instead
      // silently drops the stream, because connect() early-returns while
      // CONNECTING and the stream is then in the set but never on the wire.
      return;
    }
    this.scheduleConnect();
  }

  /**
   * Defer the first connect by a task.
   *
   * Subscriptions arrive as one synchronous burst at startup — 26 of them for
   * eight symbols. Connecting on the first one would build a URL containing
   * exactly one stream. Waiting a tick lets the whole batch land so a single
   * connection carries all of them.
   */
  private scheduleConnect(): void {
    if (this.connectTimer || this.ws || this.closedByUs) return;
    this.connectTimer = window.setTimeout(() => {
      this.connectTimer = 0;
      this.connect();
    }, 0);
  }

  remove(stream: string): void {
    if (!this.streams.delete(stream)) return;
    if (this.isOpen) {
      this.pendingUnsub.push(stream);
      this.scheduleControl();
    }
    if (this.streams.size === 0) this.shutdown();
  }

  // -- connection lifecycle -------------------------------------------------

  connect(): void {
    if (this.streams.size === 0) return;
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }
    this.clearTimer('reconnect');
    if (this.connectTimer) {
      clearTimeout(this.connectTimer);
      this.connectTimer = 0;
    }
    this.closedByUs = false;

    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      this.setState('offline');
      return;
    }

    const host = WS_HOSTS[this.hostIndex % WS_HOSTS.length];
    // The URL carries the whole stream set, so a reconnect restores every
    // subscription atomically — no replay of SUBSCRIBE messages needed.
    this.urlStreams = new Set(this.streams);
    const url = `${host}/stream?streams=${[...this.urlStreams].join('/')}`;

    this.setState(this.attempt === 0 ? 'connecting' : 'reconnecting');

    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      this.onError(err instanceof Error ? err.message : 'socket construction failed');
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      if (this.ws !== ws) return;
      this.attempt = 0;
      this.lastMessageAt = Date.now();
      this.setState('open');
      this.startWatchdog();
      this.startCycleTimer();

      // Reconcile against what the URL actually asked for. Subscriptions can
      // be added or dropped during the handshake, and those are the ones that
      // would otherwise be silently missing for the life of the connection.
      this.pendingSub.length = 0;
      this.pendingUnsub.length = 0;
      for (const s of this.streams) if (!this.urlStreams.has(s)) this.pendingSub.push(s);
      for (const s of this.urlStreams) if (!this.streams.has(s)) this.pendingUnsub.push(s);
      if (this.pendingSub.length || this.pendingUnsub.length) this.flushControl();
    };

    ws.onmessage = (ev: MessageEvent) => {
      if (this.ws !== ws) return;
      this.lastMessageAt = Date.now();

      const raw = ev.data;
      if (typeof raw !== 'string') return;

      let parsed: StreamEnvelope | { result: unknown; id: number };
      try {
        parsed = JSON.parse(raw);
      } catch {
        return;
      }

      // Control replies ({result,id}) have no `stream` field.
      const env = parsed as StreamEnvelope;
      if (typeof env.stream !== 'string') return;

      const data = env.data as { E?: number; e?: string } | undefined;
      this.onMessageMeta(data?.E, data?.e);
      this.router(env.stream, env.data);
    };

    ws.onerror = () => {
      if (this.ws !== ws) return;
      // The error event carries nothing useful in browsers; close drives retry.
      this.onError(`socket ${this.id}: transport error`);
    };

    ws.onclose = (ev: CloseEvent) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.stopWatchdog();
      this.clearTimer('cycle');
      if (this.closedByUs || this.streams.size === 0) {
        this.setState('idle');
        return;
      }
      if (ev.code !== 1000 && ev.code !== 1001) {
        this.onError(`socket ${this.id}: closed ${ev.code}${ev.reason ? ` ${ev.reason}` : ''}`);
      }
      this.scheduleReconnect();
    };
  }

  /** Drop the connection and immediately rebuild it. */
  cycle(reason: string): void {
    if (this.ws) {
      this.onError(`socket ${this.id}: ${reason}`);
      const ws = this.ws;
      this.ws = null;
      this.stopWatchdog();
      this.clearTimer('cycle');
      try {
        ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null;
        ws.close();
      } catch {
        /* already gone */
      }
    }
    this.setState('reconnecting');
    // A cycle is deliberate, so reconnect promptly rather than backing off.
    this.attempt = Math.min(this.attempt, 1);
    this.scheduleReconnect(250);
  }

  shutdown(): void {
    this.closedByUs = true;
    this.clearTimer('reconnect');
    this.clearTimer('control');
    this.clearTimer('cycle');
    if (this.connectTimer) {
      clearTimeout(this.connectTimer);
      this.connectTimer = 0;
    }
    this.stopWatchdog();
    if (this.ws) {
      const ws = this.ws;
      this.ws = null;
      try {
        ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null;
        ws.close(1000, 'client shutdown');
      } catch {
        /* ignore */
      }
    }
    this.setState('idle');
  }

  private scheduleReconnect(overrideMs?: number): void {
    if (this.closedByUs || this.streams.size === 0) return;
    this.clearTimer('reconnect');

    this.attempt++;
    // Rotate hosts once a host has clearly failed us a few times running.
    if (this.attempt > 0 && this.attempt % 3 === 0) this.hostIndex++;

    const expo = Math.min(MAX_BACKOFF, BASE_BACKOFF * 2 ** Math.min(this.attempt - 1, 6));
    // Full jitter: shards must not retry in lockstep.
    const delay = overrideMs ?? Math.random() * expo + BASE_BACKOFF * 0.5;

    this.setState('reconnecting');
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = 0;
      this.connect();
    }, delay);
  }

  private startWatchdog(): void {
    this.stopWatchdog();
    this.watchdogTimer = window.setInterval(() => {
      if (!this.isOpen) return;
      const silent = Date.now() - this.lastMessageAt;
      if (silent > STALE_MS) {
        this.cycle(`no data for ${(silent / 1000) | 0}s`);
      }
    }, 5000);
  }

  private stopWatchdog(): void {
    if (this.watchdogTimer) {
      clearInterval(this.watchdogTimer);
      this.watchdogTimer = 0;
    }
  }

  private startCycleTimer(): void {
    this.clearTimer('cycle');
    this.cycleTimer = window.setTimeout(() => {
      this.cycle('scheduled refresh before the 24h server cutoff');
    }, PROACTIVE_CYCLE_MS);
  }

  private scheduleControl(): void {
    if (this.controlTimer) return;
    this.controlTimer = window.setTimeout(() => {
      this.controlTimer = 0;
      this.flushControl();
    }, CONTROL_INTERVAL_MS);
  }

  /** Batch subscribe/unsubscribe so adding a symbol costs two frames, not eight. */
  private flushControl(): void {
    if (!this.isOpen || !this.ws) return;
    if (this.pendingSub.length) {
      const params = this.pendingSub.splice(0, this.pendingSub.length);
      this.ws.send(JSON.stringify({ method: 'SUBSCRIBE', params, id: this.nextId++ }));
    }
    if (this.pendingUnsub.length) {
      const params = this.pendingUnsub.splice(0, this.pendingUnsub.length);
      this.ws.send(JSON.stringify({ method: 'UNSUBSCRIBE', params, id: this.nextId++ }));
    }
    if (this.pendingSub.length || this.pendingUnsub.length) this.scheduleControl();
  }

  private clearTimer(which: 'reconnect' | 'control' | 'cycle'): void {
    if (which === 'reconnect' && this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = 0;
    }
    if (which === 'control' && this.controlTimer) {
      clearTimeout(this.controlTimer);
      this.controlTimer = 0;
    }
    if (which === 'cycle' && this.cycleTimer) {
      clearTimeout(this.cycleTimer);
      this.cycleTimer = 0;
    }
  }

  private setState(s: ConnState): void {
    if (this.state === s) return;
    this.state = s;
    this.onChange();
  }
}

export class StreamPool {
  private sockets: ManagedSocket[] = [];
  private refCounts = new Map<string, number>();
  private handlers = new Map<string, Set<(data: unknown) => void>>();
  private nextSocketId = 1;

  private msgWindow = 0;
  private msgWindowStart = performance.now();
  private msgRate = 0;
  private latency = new Rolling(256);
  private reconnects = 0;
  private lastError: string | null = null;
  private prevOpenCount = 0;

  readonly status = new Emitter<PoolStatus>();
  /** Fires whenever a socket transitions from not-open to open. */
  readonly reconnected = new Emitter<void>();

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        this.lastError = null;
        for (const s of this.sockets) s.connect();
        this.publish();
      });
      window.addEventListener('offline', () => {
        this.lastError = 'browser reports offline';
        this.publish();
      });
      document.addEventListener('visibilitychange', () => {
        // Background tabs get timers throttled to ~1/min, so the watchdog may
        // not have fired. Verify liveness the moment we are visible again.
        if (!document.hidden) this.verify();
      });
    }
  }

  /** Subscribe to a stream. Returns an unsubscribe function. */
  subscribe(stream: string, handler: (data: unknown) => void): () => void {
    let set = this.handlers.get(stream);
    if (!set) {
      set = new Set();
      this.handlers.set(stream, set);
    }
    set.add(handler);

    const rc = (this.refCounts.get(stream) ?? 0) + 1;
    this.refCounts.set(stream, rc);
    if (rc === 1) this.attach(stream);

    let released = false;
    return () => {
      if (released) return;
      released = true;
      const s = this.handlers.get(stream);
      s?.delete(handler);
      const next = (this.refCounts.get(stream) ?? 1) - 1;
      if (next <= 0) {
        this.refCounts.delete(stream);
        this.handlers.delete(stream);
        this.detach(stream);
      } else {
        this.refCounts.set(stream, next);
      }
    };
  }

  private attach(stream: string): void {
    let target = this.sockets.find((s) => s.size < MAX_STREAMS_PER_SOCKET);
    if (!target) {
      target = new ManagedSocket(
        this.nextSocketId++,
        this.route,
        this.onSocketChange,
        this.onMessageMeta,
        this.noteError,
      );
      this.sockets.push(target);
    }
    target.add(stream);
  }

  private detach(stream: string): void {
    const owner = this.sockets.find((s) => s.has(stream));
    if (!owner) return;
    owner.remove(stream);
    if (owner.size === 0) {
      this.sockets = this.sockets.filter((s) => s !== owner);
      this.publish();
    }
  }

  private route: Router = (stream, data) => {
    const set = this.handlers.get(stream);
    if (!set) return;
    for (const fn of set) {
      try {
        fn(data);
      } catch (err) {
        console.error('[pool] handler threw for', stream, err);
      }
    }
  };

  private onMessageMeta = (eventTime: number | undefined, eventType?: string): void => {
    this.msgWindow++;
    const now = performance.now();
    const elapsed = now - this.msgWindowStart;
    if (elapsed >= 1000) {
      // Exponential smoothing: raw per-second counts are visually noisy.
      const inst = (this.msgWindow * 1000) / elapsed;
      this.msgRate = this.msgRate === 0 ? inst : this.msgRate * 0.6 + inst * 0.4;
      this.msgWindow = 0;
      this.msgWindowStart = now;
      this.publishThrottled();
    }
    // Latency is sampled only from streams that are pushed the moment
    // something happens. `@ticker` and `@kline` carry the event time of a
    // rolling aggregation window, so they appear up to a second stale even on
    // a perfect connection — mixing them in makes the readout describe
    // Binance's batching interval rather than the link.
    if (eventTime !== undefined && (eventType === 'trade' || eventType === 'depthUpdate')) {
      // Measured against the exchange clock, not this machine's — see
      // `syncClock` in binance.ts for why that distinction matters.
      const d = serverNow() - eventTime;
      if (d > -2000 && d < 60_000) this.latency.push(d);
    }
  };

  private onSocketChange = (): void => {
    const openNow = this.sockets.filter((s) => s.isOpen).length;
    if (openNow > this.prevOpenCount && this.prevOpenCount >= 0) {
      // A socket came up. If it is not the very first connect of its life,
      // treat it as a reconnect so consumers can resync stateful streams.
      if (this.reconnectsPending) {
        this.reconnects++;
        this.reconnected.emit();
      }
      this.reconnectsPending = false;
    }
    if (openNow < this.prevOpenCount) this.reconnectsPending = true;
    this.prevOpenCount = openNow;
    this.publish();
  };

  private reconnectsPending = false;

  private noteError = (msg: string): void => {
    this.lastError = msg;
    this.reconnectsPending = true;
    this.publish();
  };

  /** Force every socket to prove it is alive, cycling the ones that are not. */
  verify(): void {
    for (const s of this.sockets) {
      if (!s.isOpen) s.connect();
    }
  }

  /** Tear down and rebuild every socket. Wired to the UI's reconnect button. */
  reconnectAll(reason = 'manual reconnect'): void {
    this.reconnectsPending = true;
    for (const s of this.sockets) s.cycle(reason);
  }

  shutdown(): void {
    for (const s of this.sockets) s.shutdown();
    this.sockets = [];
    this.handlers.clear();
    this.refCounts.clear();
    this.publish();
  }

  snapshot(): PoolStatus {
    const open = this.sockets.filter((s) => s.isOpen).length;
    const total = this.sockets.length;
    let state: ConnState;
    if (total === 0) state = 'idle';
    else if (typeof navigator !== 'undefined' && navigator.onLine === false) state = 'offline';
    else if (open === total) state = 'open';
    else if (open > 0) state = 'reconnecting';
    else state = this.sockets.some((s) => s.state === 'connecting') ? 'connecting' : 'reconnecting';

    return {
      state,
      open,
      total,
      streams: this.refCounts.size,
      msgRate: this.msgRate,
      latency: this.latency.percentile(0.5),
      reconnects: this.reconnects,
      lastError: this.lastError,
    };
  }

  private publishTimer = 0;
  private publishThrottled(): void {
    if (this.publishTimer) return;
    this.publishTimer = window.setTimeout(() => {
      this.publishTimer = 0;
      this.publish();
    }, 500);
  }

  private publish(): void {
    this.status.emit(this.snapshot());
  }
}

export const pool = new StreamPool();
